import { Injectable } from '@angular/core';
import { ConfigService } from './config.service';
import { HttpClient } from '@angular/common/http';
import { RoomBasicNode } from '../locations/room.service';

export interface RoomTemplate {
  name: string;
  id: string;
  location_id: string;
  last_update_time: number;
  version_number: TemplateVersions;
  automation_sets: Array<{
    template_autoset_id?: string; // V3 and up
    name: string;
    sensors: Array<{
      name: string;
      type: string;
      template_node_id?: string;
    }>;
  }>;
  meta_climate?: ClimateMetaData; // only V3 and up
}

// interface from DB
export interface ClimateMetaData {
  climate_version: ClimateVersions;
  custom_v4_maps?: {
    // each key=>value is template_autoset_id=>map
    [key: string]: AutosetClimateMap;
  };
}

export interface AutosetClimateMap {
  ambient_temp_template_node_id: string;
  pressure_template_node_id: string;
  humidity_template_node_id: string;
  dewpoint_template_node_id: string;
}

// internal interface used by components to render room climate data
export interface ClimateStream {
  stream_id: string;
  type: ReadingsNodeTypes;
}

export interface ClimateStreamsMap {
  climateAmbTempStream: ClimateStream;
  climateHumidityStream: ClimateStream;
  climatePressureStream: ClimateStream;
  climateDewPointStream: ClimateStream;
}

export enum ReadingsNodeTypes {
  AC = 'AC',
  OCC = 'OCC'
}

export enum TemplateVersions {
  V2 = 'V2', // represented by undefined on backend
  V3 = 'V3',
  V4 = 'V4'
}

export enum ClimateVersions {
  DEFAULT_V2 = 'DEFAULT_V2', // legacy default, means autoset climate from each AC sensor, represented by undefined on backend
  DEFAULT_V3 = 'DEFAULT_V3', // current default, means autoset climate fr ONE occ sensor (first)
  CUSTOM_V4 = 'CUSTOM_V4' // new custom, means choose autoset climate based on template meta
}

@Injectable()
export class ClimateReadingsService {
  constructor(private config: ConfigService, private http: HttpClient) {}

  /* 
  Room Template Methods - better belong elsewhere in future
  */
  public async getRoomTemplateById(room_template_id: any, location_id: any) {
    const url = this.config.getRoomTemplateUrl() + `/room-templates/${room_template_id}/location/${location_id}`;
    const roomTemplate = await this.http.get<RoomTemplate>(url).toPromise();
    return roomTemplate;
  }

  public async listRoomTemplatesByLocation(location_id: string) {
    const url = this.config.getRoomTemplateUrl() + `/room-templates/list/${location_id}`;
    const roomTemplates = await this.http.get<RoomTemplate[]>(url).toPromise();
    return roomTemplates;
  }

  /*
  Methods for telling IOT which node to connect to for climate readings
  Assume one climate sensor for one autoset
  */
  public validateClimateStreams(climateStreamsMap: ClimateStreamsMap) {
    const isValid = !!(
      climateStreamsMap.climateAmbTempStream &&
      climateStreamsMap.climateAmbTempStream.stream_id &&
      climateStreamsMap.climateAmbTempStream.type &&
      climateStreamsMap.climateDewPointStream &&
      climateStreamsMap.climateDewPointStream.stream_id &&
      climateStreamsMap.climateDewPointStream.type &&
      climateStreamsMap.climateHumidityStream &&
      climateStreamsMap.climateHumidityStream.stream_id &&
      climateStreamsMap.climateHumidityStream.type &&
      climateStreamsMap.climatePressureStream &&
      climateStreamsMap.climatePressureStream.stream_id &&
      climateStreamsMap.climatePressureStream.type
    );
    return isValid;
  }

  public getClimateShadowId(climateStreamsMap: ClimateStreamsMap) {
    const ambTempStreamId = climateStreamsMap.climateAmbTempStream.stream_id;
    // e.g. stream-FFFF90FD9FDDC67B-4, assume same for all measurements
    const nodeId = ambTempStreamId.split('-')[1];
    return `node-${nodeId}`;
  }

  public isAutosetClimateFromOcc(climateStreamsMap: ClimateStreamsMap) {
    const ambTempNodeType = climateStreamsMap.climateAmbTempStream.type;
    const isFromOcc = ambTempNodeType === ReadingsNodeTypes.OCC;
    return isFromOcc;
  }

  public getClimateMetaFromRoomTemplate(roomTemplate: RoomTemplate): ClimateMetaData {
    const templateVer = roomTemplate.version_number;
    const isTemplateVersion3AndUp = templateVer && templateVer >= TemplateVersions.V3 && roomTemplate.meta_climate;

    if (isTemplateVersion3AndUp) {
      return roomTemplate.meta_climate;
    }
    // else patch climateMeta as V2 for older versions with missing data
    return {
      climate_version: ClimateVersions.DEFAULT_V2
    };
  }

  // entry point method for resolving which stream has which measurement
  // encapsulates all the logic for sorting, then selecting the correct stream from the pool of AC/OCC streams
  // right now, all the measurements MUST originate from the same sensor if user chooses use CUSTOM_V4
  // this is controlled on the input side (install app)
  public getClimateStreamsForAutosetFromNodes(
    climateMeta: ClimateMetaData,
    templateAutosetId: string,
    acNodes: RoomBasicNode[],
    occNodes: RoomBasicNode[]
  ): ClimateStreamsMap {
    console.warn(`Now parsing climate streams using meta data: ${climateMeta.climate_version}`);
    this.validateClimateAndRoomTemplateBothV4(climateMeta, templateAutosetId);

    const climateAmbTempStream = this.getAmbientTempDataStreamId(templateAutosetId, climateMeta, acNodes, occNodes);
    const climateHumidityStream = this.getHumidityDataStreamId(templateAutosetId, climateMeta, acNodes, occNodes);
    const climatePressureStream = this.getPresssureDataStreamId(templateAutosetId, climateMeta, acNodes, occNodes);
    const climateDewPointStream = this.getDewPointDataStreamId(templateAutosetId, climateMeta, acNodes, occNodes);

    return {
      climateAmbTempStream,
      climateHumidityStream,
      climatePressureStream,
      climateDewPointStream
    };
  }

  // Business logic for selecting the ambientTempData stream
  private getAmbientTempDataStreamId(
    templateAutosetId: string,
    climateMeta: ClimateMetaData,
    acNodes: RoomBasicNode[],
    occNodes: RoomBasicNode[]
  ): ClimateStream {
    let stream_id: string; // e.g. stream-FFFF90FD9FDDC67B-3
    let type: ReadingsNodeTypes;

    const version: ClimateVersions = climateMeta.climate_version;

    const getTempStreamIdForAc = acNodeId => `stream-${acNodeId}-3`; // #3 on ac
    const getTempStreamIdForOcc = occNodeId => `stream-${occNodeId}-1`; // #1 on occ

    try {
      switch (version) {
        case ClimateVersions.DEFAULT_V2: // e.g. 5FootWay
          // in old default, there is only 1 AC per autoset, so no point sorting the AC, choose single AC node
          stream_id = getTempStreamIdForAc(acNodes[0].node_id);
          type = ReadingsNodeTypes.AC;
          break;
        case ClimateVersions.DEFAULT_V3: // e.g. Alila Uluwatu
          // in new default, we sort the occ streams by by name and take the first occ node
          occNodes.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
          stream_id = getTempStreamIdForOcc(occNodes[0].node_id);
          type = ReadingsNodeTypes.OCC;
          break;
        case ClimateVersions.CUSTOM_V4:
          const templateNodeId = climateMeta['custom_v4_maps'][templateAutosetId]['ambient_temp_template_node_id'];
          let acNode, occNode;
          if (acNodes && acNodes.length) {
            acNode = acNodes.find(n => n.template_node_id === templateNodeId);
          }
          if (occNodes && occNodes.length) {
            occNode = occNodes.find(n => n.template_node_id === templateNodeId);
          }
          if (acNode) {
            stream_id = getTempStreamIdForAc(acNode.node_id);
            type = ReadingsNodeTypes.AC;
          } else if (occNode) {
            stream_id = getTempStreamIdForOcc(occNode.node_id);
            type = ReadingsNodeTypes.OCC;
          } else {
            throw new Error(`Did not find template_node_id ${templateNodeId} among installed nodes, using CUSTOM_V4 rendering`);
          }
          break;
        default:
          throw new Error(`Error: Unrecognised source of climate readings: ${JSON.stringify(climateMeta, null, 2)}`);
      }
    } catch (error) {
      console.error(`Cannot resolve correct Node to determine Ambient Temp Stream: ${error.message}`);
      return null;
    }
    return { stream_id, type };
  }

  private getDewPointDataStreamId(
    templateAutosetId: string,
    climateMeta: ClimateMetaData,
    acNodes: RoomBasicNode[],
    occNodes: RoomBasicNode[]
  ): ClimateStream {
    let stream_id: string; // e.g. stream-FFFF90FD9FDDC67B-3
    let type: ReadingsNodeTypes;
    const version = climateMeta.climate_version;

    const getDewStreamIdForAc = acNodeId => `stream-${acNodeId}-6`; // #6 on ac
    const getDewStreamIdForOcc = occNodeId => `stream-${occNodeId}-7`; // #7 on occ

    try {
      switch (version) {
        case ClimateVersions.DEFAULT_V2: // e.g. 5FootWay
          stream_id = getDewStreamIdForAc(acNodes[0].node_id);
          type = ReadingsNodeTypes.AC;
          break;
        case ClimateVersions.DEFAULT_V3: // e.g. Alila Uluwatu
          occNodes.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
          stream_id = getDewStreamIdForOcc(occNodes[0].node_id);
          type = ReadingsNodeTypes.OCC;
          break;
        case ClimateVersions.CUSTOM_V4:
          const templateNodeId = climateMeta['custom_v4_maps'][templateAutosetId]['dewpoint_template_node_id'];
          let acNode, occNode;
          if (acNodes && acNodes.length) {
            acNode = acNodes.find(n => n.template_node_id === templateNodeId);
          }
          if (occNodes && occNodes.length) {
            occNode = occNodes.find(n => n.template_node_id === templateNodeId);
          }
          if (acNode) {
            stream_id = getDewStreamIdForAc(acNode.node_id);
            type = ReadingsNodeTypes.AC;
          } else if (occNode) {
            stream_id = getDewStreamIdForOcc(occNode.node_id);
            type = ReadingsNodeTypes.OCC;
          } else {
            throw new Error(`Template node id ${templateNodeId} among installed Nodes, V4`);
          }
          break;
        default:
          throw new Error(`Error: Unrecognised source of climate readings: ${JSON.stringify(climateMeta, null, 2)}`);
      }
    } catch (error) {
      console.error(`Cannot resolve correct Node to determine Dew Point Stream: ${error.message}`);
      return null;
    }

    return { stream_id, type };
  }

  private getHumidityDataStreamId(
    templateAutosetId: string,
    climateMeta: ClimateMetaData,
    acNodes: RoomBasicNode[],
    occNodes: RoomBasicNode[]
  ): ClimateStream {
    let stream_id: string; // e.g. stream-FFFF90FD9FDDC67B-3
    let type: ReadingsNodeTypes;

    const version = climateMeta.climate_version;

    const getHumidityStreamIdForAc = acNodeId => `stream-${acNodeId}-4`; // #4 on ac
    const getHumidityStreamIdForOcc = occNodeId => `stream-${occNodeId}-5`; // #5 on occ

    try {
      switch (version) {
        case ClimateVersions.DEFAULT_V2: // e.g. 5FootWay
          stream_id = getHumidityStreamIdForAc(acNodes[0].node_id);
          type = ReadingsNodeTypes.AC;
          break;
        case ClimateVersions.DEFAULT_V3: // e.g. Alila Uluwatu
          occNodes.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
          stream_id = getHumidityStreamIdForOcc(occNodes[0].node_id);
          type = ReadingsNodeTypes.OCC;
          break;
        case ClimateVersions.CUSTOM_V4:
          const templateNodeId = climateMeta['custom_v4_maps'][templateAutosetId]['humidity_template_node_id'];
          let acNode, occNode;
          if (acNodes && acNodes.length) {
            acNode = acNodes.find(n => n.template_node_id === templateNodeId);
          }
          if (occNodes && occNodes.length) {
            occNode = occNodes.find(n => n.template_node_id === templateNodeId);
          }
          if (acNode) {
            stream_id = getHumidityStreamIdForAc(acNode.node_id);
            type = ReadingsNodeTypes.AC;
          } else if (occNode) {
            stream_id = getHumidityStreamIdForOcc(occNode.node_id);
            type = ReadingsNodeTypes.OCC;
          } else {
            throw new Error(`Template node id ${templateNodeId} among installed nodes CUSTOM_V4 rendering`);
          }
          break;
        default:
          throw new Error(`Error: Unrecognised source of climate readings: ${JSON.stringify(climateMeta, null, 2)}`);
      }
    } catch (error) {
      console.error(`Cannot resolve correct Node to determine humidity Stream: ${error.message}`);
      return null;
    }
    return { stream_id, type };
  }

  private getPresssureDataStreamId(
    templateAutosetId: string,
    climateMeta: ClimateMetaData,
    acNodes: RoomBasicNode[],
    occNodes: RoomBasicNode[]
  ): ClimateStream {
    let stream_id: string; // e.g. stream-FFFF90FD9FDDC67B-3
    let type: ReadingsNodeTypes;
    const version = climateMeta.climate_version;

    const getPressureStreamIdForAc = acNodeId => `stream-${acNodeId}-5`; // #5 on ac
    const getPressureStreamIdForOcc = occNodeId => `stream-${occNodeId}-6`; // #6 on occ

    try {
      switch (version) {
        case ClimateVersions.DEFAULT_V2: // e.g. 5FootWay
          stream_id = getPressureStreamIdForAc(acNodes[0].node_id);
          type = ReadingsNodeTypes.AC;
          break;
        case ClimateVersions.DEFAULT_V3: // e.g. Alila Uluwatu
          occNodes.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
          stream_id = getPressureStreamIdForOcc(occNodes[0].node_id);
          type = ReadingsNodeTypes.OCC;
          break;
        case ClimateVersions.CUSTOM_V4:
          const templateNodeId = climateMeta['custom_v4_maps'][templateAutosetId]['pressure_template_node_id'];
          let acNode, occNode;
          if (acNodes && acNodes.length) {
            acNode = acNodes.find(n => n.template_node_id === templateNodeId);
          }
          if (occNodes && occNodes.length) {
            occNode = occNodes.find(n => n.template_node_id === templateNodeId);
          }
          if (acNode) {
            stream_id = getPressureStreamIdForAc(acNode.node_id);
            type = ReadingsNodeTypes.AC;
          } else if (occNode) {
            stream_id = getPressureStreamIdForOcc(occNode.node_id);
            type = ReadingsNodeTypes.OCC;
          } else {
            throw new Error(`Template node id ${templateNodeId} not found among installed nodes, for CUSTOM_V4 rendering`);
          }
          break;
        default:
          throw new Error(`Error: Unrecognised source of climate readings: ${JSON.stringify(climateMeta, null, 2)}`);
      }
    } catch (error) {
      console.error(`Cannot resolve correct Node to determine pressure Stream: ${error.message}`);
      return null;
    }
    return { stream_id, type };
  }

  private validateClimateAndRoomTemplateBothV4(climateMeta: ClimateMetaData, templateAutosetId: string) {
    // expect autosets created with V4 templates to have template_autoset_id
    if (climateMeta.climate_version === ClimateVersions.CUSTOM_V4) {
      if (templateAutosetId == null) {
        throw new Error(`Room template versions & climate meta versions do match`);
      }
    }
    return true; // continue
  }
}



// WEBPACK FOOTER //
// /Users/sensorflow/Documents/legacy/kalinda/Dashboard/sensorflow-dashboard/src/app/core/climate-readings.service.ts